import { Children, ReactNode, useCallback, useEffect, useState } from "react";
import { useRemembered } from "@mywheels/common/util";

import styles from "./Masonry.module.css";

type Props = {
  defaultNumCols?: number;
  breakpoints?: Record<number, number>;
  children?: ReactNode;
};

export function Masonry({
  defaultNumCols = 2,
  breakpoints: _breakpoints = {},
  children,
}: Props) {
  const [numCols, setNumCols] = useState(defaultNumCols);

  const breakpoints = useRemembered(_breakpoints);

  const reCalculateColumnCount = useCallback(() => {
    const windowWidth = (window && window.innerWidth) || Infinity;

    setNumCols(
      Math.max(
        1,
        Object.entries(breakpoints)
          .sort((a, b) => b[1] - a[1])
          .find((e) => {
            const px = parseInt(e[0]);
            return px > 0 && windowWidth > px;
          })?.[1] || defaultNumCols
      )
    );
  }, [setNumCols, breakpoints, defaultNumCols]);

  useEffect(() => {
    reCalculateColumnCount();

    let _lastRecalculateAnimationFrame: number;

    const reCalculateColumnCountDebounce = () => {
      if (!window || !window.requestAnimationFrame) {
        // IE10+
        reCalculateColumnCount();
        return;
      }

      if (window.cancelAnimationFrame) {
        // IE10+
        window.cancelAnimationFrame(_lastRecalculateAnimationFrame);
      }

      _lastRecalculateAnimationFrame = window.requestAnimationFrame(() => {
        reCalculateColumnCount();
      });
    };

    if (window?.addEventListener) {
      window.addEventListener("resize", reCalculateColumnCountDebounce);
      return () => {
        window.removeEventListener("resize", reCalculateColumnCountDebounce);
      };
    }
  }, [reCalculateColumnCount]);

  const itemsInColumns = () => {
    const itemsInColumns = new Array(numCols);

    // Force children to be handled as an array
    const items = Children.toArray(children);

    for (let i = 0; i < items.length; i++) {
      const columnIndex = i % numCols;

      if (!itemsInColumns[columnIndex]) {
        itemsInColumns[columnIndex] = [];
      }

      itemsInColumns[columnIndex].push(items[i]);
    }

    return itemsInColumns;
  };

  const renderColumns = () => {
    const childrenInColumns = itemsInColumns();
    const columnWidth = `${100 / childrenInColumns.length}%`;

    const columnAttributes = {
      // NOTE: the column property is undocumented and considered deprecated.
      // It is an alias of the `columnAttrs` property
      // ...column,
      // ...columnAttrs,
      style: {
        // ...columnAttrs.style,
        width: columnWidth,
      },
      className: styles.grid_column,
    };

    return childrenInColumns.map((items, i) => {
      return (
        <div {...columnAttributes} key={i}>
          {items}
        </div>
      );
    });
  };

  return <div className={styles.grid}>{renderColumns()}</div>;
}
